import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { DS_ICONS, DsIconDefinition, DsIconName } from './ds-icons';

export type DsIconSize = number | `${number}px` | `var(${string})`;

let nextDsIconGradientId = 0;

@Component({
  selector: 'ds-icon',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <svg
      class="ds-icon"
      [style.--ds-icon-size]="resolvedSize"
      [attr.viewBox]="icon?.viewBox || defaultViewBox"
      [attr.aria-hidden]="ariaLabel ? null : 'true'"
      [attr.aria-label]="ariaLabel || null"
      [attr.role]="ariaLabel ? 'img' : null"
      focusable="false"
    >
      @if (hasGradientBackground) {
        <defs>
          <linearGradient [attr.id]="gradientId" x1="-100%" y1="-100%" x2="0%" y2="0%">
            <stop offset="0%" stop-color="var(--ds-icon-gradient-background-start-color)" />
            <stop offset="25%" stop-color="var(--ds-icon-gradient-background-middle-color)" />
            <stop offset="50%" stop-color="var(--ds-icon-gradient-background-end-color)" />
            <stop offset="75%" stop-color="var(--ds-icon-gradient-background-middle-color)" />
            <stop offset="100%" stop-color="var(--ds-icon-gradient-background-start-color)" />
            <animate attributeName="x1" values="-100%;0%;-100%" keyTimes="0;0.5;1" dur="5600ms" repeatCount="indefinite" />
            <animate attributeName="y1" values="-100%;0%;-100%" keyTimes="0;0.5;1" dur="5600ms" repeatCount="indefinite" />
            <animate attributeName="x2" values="0%;100%;0%" keyTimes="0;0.5;1" dur="5600ms" repeatCount="indefinite" />
            <animate attributeName="y2" values="0%;100%;0%" keyTimes="0;0.5;1" dur="5600ms" repeatCount="indefinite" />
          </linearGradient>
        </defs>
      }
      @for (path of icon?.paths || []; track path) {
        <path [attr.d]="path" [attr.fill]="pathFill"></path>
      }
    </svg>
  `,
  styles: [`
    :host {
      display: inline-flex;
      flex: 0 0 auto;
      color: currentColor;
      line-height: 0;
    }

    .ds-icon {
      display: inline-flex;
      width: var(--ds-icon-size);
      height: var(--ds-icon-size);
      flex: 0 0 var(--ds-icon-size);
      overflow: hidden;
      fill: currentColor;
    }
  `],
})
export class DsIconComponent {
  @Input() name: DsIconName | string = 'VisibilityFill';
  @Input() size: DsIconSize = 24;
  @Input() fill = false;
  @Input() ariaLabel = '';
  @Input() gradientBackground: boolean | string = false;

  readonly gradientId = `ds-icon-gradient-background-${nextDsIconGradientId++}`;

  readonly defaultViewBox = '0 0 24 24';

  get icon(): DsIconDefinition | undefined {
    const icon = this.resolveIcon(this.name);
    if (!icon && this.fill) {
      return this.resolveIcon(`${this.name}-fill`);
    }

    if (!this.fill || !icon || icon.materialName.endsWith('-fill')) {
      return icon;
    }

    return this.resolveIcon(`${icon.materialName}-fill`) ?? icon;
  }

  get resolvedSize(): string {
    return typeof this.size === 'number' ? `${this.size}px` : this.size;
  }

  get hasGradientBackground(): boolean {
    return this.gradientBackground === true || this.gradientBackground === 'true';
  }

  get pathFill(): string | null {
    return this.hasGradientBackground ? `url(#${this.gradientId})` : null;
  }

  private resolveIcon(name: string): DsIconDefinition | undefined {
    return DS_ICONS[name as DsIconName]
      ?? Object.values(DS_ICONS).find((icon) => icon.materialName === name);
  }
}
